import PropTypes from 'prop-types';
import cx from 'classnames';

import sty from './Sparkline.module.scss';

// Per-instance ids — multiple Sparklines on one page would otherwise share
// gradient / clip-path ids and the second instance's fill would be wrong.
let seq = 0;

// Build a smooth cubic-Bézier path from a list of points using the
// Catmull-Rom → Bézier conversion. Produces gentle curves through every
// data point (no overshoot at peaks), which reads as "production dashboard"
// rather than a wireframe polyline.
function smoothPath(points) {
  if (points.length === 0) return '';
  if (points.length === 1) return `M ${points[0][0]} ${points[0][1]}`;
  const out = [`M ${points[0][0]} ${points[0][1]}`];
  for (let i = 0; i < points.length - 1; i += 1) {
    const p0 = points[i === 0 ? i : i - 1];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[i + 2 < points.length ? i + 2 : i + 1];
    const c1x = p1[0] + (p2[0] - p0[0]) / 6;
    const c1y = p1[1] + (p2[1] - p0[1]) / 6;
    const c2x = p2[0] - (p3[0] - p1[0]) / 6;
    const c2y = p2[1] - (p3[1] - p1[1]) / 6;
    out.push(`C ${c1x} ${c1y}, ${c2x} ${c2y}, ${p2[0]} ${p2[1]}`);
  }
  return out.join(' ');
}

function Sparkline({ data, compare, color, width, height, ariaLabel, className }) {
  if (!Array.isArray(data) || data.length === 0) {
    return <div className={cx(sty.empty, className)}>No data yet</div>;
  }
  const w = width;
  const h = height;
  const all = Array.isArray(compare) && compare.length > 0 ? [...data, ...compare] : data;
  const max = Math.max(...all);
  const min = Math.min(...all);
  const span = max - min || 1;

  // Reserve a 14px top margin for the peak-value label and a 4px bottom for
  // the area to breathe; data plots inside this inner band.
  const padTop = 16;
  const padBottom = 4;
  const plotH = h - padTop - padBottom;

  const toPts = arr =>
    arr.map((v, i) => [
      (i / Math.max(arr.length - 1, 1)) * w,
      padTop + (1 - (v - min) / span) * plotH,
    ]);

  const pts = toPts(data);
  const linePath = smoothPath(pts);
  // Close the area down to the baseline so the gradient fills below the curve.
  const areaPath = `${linePath} L ${pts[pts.length - 1][0]} ${h} L 0 ${h} Z`;
  const comparePath =
    Array.isArray(compare) && compare.length > 0 ? smoothPath(toPts(compare)) : null;

  const stroke = color || 'var(--brand-primary, #009de1)';
  // eslint-disable-next-line no-plusplus
  const id = ++seq;
  const gradId = `sparkGrad-${id}`;

  // Peak — render dot + value label slightly above so the maximum is visible
  // without hovering. Text x is nudged to stay inside the viewBox.
  const peakIdx = data.indexOf(Math.max(...data));
  const [peakX, peakY] = pts[peakIdx];
  const peakVal = data[peakIdx];
  const peakLabelX = Math.max(12, Math.min(w - 12, peakX));
  let peakAnchor = 'middle';
  if (peakX < 24) peakAnchor = 'start';
  else if (peakX > w - 24) peakAnchor = 'end';

  // Subtle horizontal baselines at 25/50/75 % of the plot area. Hairline
  // strokes that anchor the curve in space without competing with it.
  const gridYs = [0.25, 0.5, 0.75].map(t => padTop + t * plotH);

  return (
    <svg
      className={cx(sty.svg, className)}
      width="100%"
      height={h}
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="none"
      role="img"
      aria-label={ariaLabel}
    >
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={stroke} stopOpacity="0.32" />
          <stop offset="100%" stopColor={stroke} stopOpacity="0" />
        </linearGradient>
      </defs>
      {gridYs.map(y => (
        <line key={y} x1="0" x2={w} y1={y} y2={y} stroke="#eef1f3" strokeWidth="1" />
      ))}
      <path d={areaPath} fill={`url(#${gradId})`} />
      {comparePath && (
        <path
          d={comparePath}
          fill="none"
          stroke="#c6cfd3"
          strokeWidth="1.5"
          strokeDasharray="3 3"
          strokeLinejoin="round"
          strokeLinecap="round"
        />
      )}
      <path
        d={linePath}
        fill="none"
        stroke={stroke}
        strokeWidth="2"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      <circle cx={peakX} cy={peakY} r="3.5" fill={stroke} />
      <circle cx={peakX} cy={peakY} r="6" fill={stroke} fillOpacity="0.18" />
      <text
        x={peakLabelX}
        y={Math.max(11, peakY - 8)}
        textAnchor={peakAnchor}
        className={sty.peakLabel}
        fill={stroke}
      >
        {peakVal.toLocaleString()}
      </text>
    </svg>
  );
}

Sparkline.propTypes = {
  data: PropTypes.arrayOf(PropTypes.number).isRequired,
  compare: PropTypes.arrayOf(PropTypes.number),
  color: PropTypes.string,
  width: PropTypes.number,
  height: PropTypes.number,
  ariaLabel: PropTypes.string,
  className: PropTypes.string,
};

Sparkline.defaultProps = {
  compare: null,
  color: undefined,
  width: 320,
  // Taller default — the redesigned chart cards sit beside funnels/donuts
  // (~140-160px tall content). 80px sparklines looked stranded under their
  // own card chrome; 140 matches the surrounding rhythm.
  height: 140,
  ariaLabel: 'Trend',
  className: undefined,
};

export default Sparkline;
